import { Tooltip } from '@components/tooltip';
import { ExternalLinkIcon } from '@sketchnote/icons';
import { Avatar, AvatarGroup, Checkbox, renderPriorities } from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import format from 'date-fns/format';
import { memo } from 'react';
import {
  ApprovalMessage,
  ApprovalTagColor,
} from 'src/features/tasks/components/ApprovalComponents';

import { useBoardStore } from '../boards';

export const TableColor = {
  '#fbb6ce': '#EC7DD5',
  '#d6bcfa': '#18B4E6',
  '#81e6d9': '#81e6d9',
  '#86E495': '#7AA00D',
  '#9BADEE': '#211650',
  '#fbd38d': '#FD9740',
  '#FFE98F': '#FFE98F',
  '#FBB1A7': '#FBB1A7',
};

export const DateRenderer = ({ value }: { value: any[] }) => {
  const dateFormat = 'do MMM yyyy';

  const handleDatesForInput = (values) => {
    if (!values) return '';
    if (values.length < 1) return '';
    else if (values.length === 1) return `${format(new Date(values[0]), dateFormat)}`;
    else if (values.length > 1)
      return `${format(new Date(values[0]), dateFormat)} - ${format(
        new Date(values[1]),
        dateFormat
      )}`;
    return '';
  };

  return <span>{handleDatesForInput(value)}</span>;
};

export const AssigneeRenderer = ({ value }: { value: any }) => {
  return (
    <span className="flex h-full items-center">
      <AvatarGroup maxCount={4}>
        {value?.map((assignee, index) => (
          <Avatar
            size="sm"
            type="photo"
            src={assignee.avatar}
            name={assignee.firstname}
            key={`{${assignee.memberId}-${index}}`}
            wrapperClassName="-mr-8 border-2 border-white transform transition duration-500 hover:scale-125"
          />
        ))}
      </AvatarGroup>
    </span>
  );
};

export const PriorityRenderer = ({ value }: { value: 'low' | 'high' | 'medium' }) => {
  return (
    <div className="flex items-center">
      <span className="mr-8 mt-2 block">{renderPriorities[value]}</span>
      <span className="text-sm block capitalize">{value}</span>
    </div>
  );
};

export const LabelRenderer = ({ data }: { data: any }) => {
  const value = data?.label || [];
  return (
    <div className="h-full w-full flex items-center">
      {value?.length > 0 ? (
        <div className="flex gap-6">
          <div
            style={{ backgroundColor: TableColor[value[0]?.color] }}
            className="leading-10 px-6 py-4 text-xs font-semibold text-white rounded-sm block mr-4 max-w-64 truncate"
          >
            {value[0]?.label}
          </div>
          {value.length > 1 && (
            <div
              style={{ backgroundColor: TableColor[value[1]?.color] }}
              className="leading-10 px-6 py-4 text-xs font-semibold text-white rounded-sm block mr-4 max-w-64 truncate"
            >
              {value[1]?.label}
            </div>
          )}
        </div>
      ) : (
        <div />
      )}

      {value?.length > 2 ? (
        <div className="px-5 text-xs rounded-sm block text-gray-700">+{value.length - 2}</div>
      ) : null}
    </div>
  );
};

export const ApprovalRenderer = ({ value }: { value: any }) => {
  return (
    <div
      className={`${ApprovalTagColor[value]} flex w-full h-full p-8 items-center m-auto text-xs capitalize font-medium truncate`}
    >
      {ApprovalMessage[value]}
    </div>
  );
};

const OpenTaskColumnButtonBase = ({ value }: { value: string }) =>
  value ? (
    <Tippy content={<span className="text-xs">Open task</span>}>
      <div className="flex w-full h-full justify-center items-center cursor-pointer text-gray-500 hover:text-gray-700 ">
        <ExternalLinkIcon />
      </div>
    </Tippy>
  ) : (
    <div />
  );
export const OpenTaskColumnButton = memo(OpenTaskColumnButtonBase);

export const StatusRenderer = ({ data, value }: { data: any; value: string }) => {
  return (
    <div className=" flex items-center h-full">
      <span
        className="text-xs text-gray-700 font-medium bg-primary-200 rounded-sm p-6 leading-10 mr-4"
        style={{ backgroundColor: data?.color || 'transparent' }}
      >
        {value}
      </span>
    </div>
  );
};

export const RowSelectionRenderer = ({ data }: { data: any; value: boolean }) => {
  const selectedItems = useBoardStore((s) => s.selectedItems);
  const toggleSelectedItem = useBoardStore((s) => s.toggleSelectedItem);
  const selected = selectedItems.includes(data?.id);

  if (data.parent || !data.id) return null;

  return (
    <Tooltip position="top center" title="Select tasks to delete or move to another file">
      <div className="flex items-center h-full">
        <Checkbox
          size="small"
          onChange={() => {
            toggleSelectedItem(data.id);
          }}
          checked={selected}
        />
      </div>
    </Tooltip>
  );
};
